import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export type Theme = "dark" | "light";

export interface Notification {
  id: string;
  type: "trade" | "system" | "security" | "deposit" | "withdrawal";
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  actionUrl?: string;
}

export interface UiState {
  theme: Theme;
  sidebarOpen: boolean;
  notifications: Notification[];
  unreadNotificationsCount: number;
  searchOpen: boolean;
  profileDropdownOpen: boolean;
  notificationPanelOpen: boolean;
  activeModal: string | null;
  toasts: Array<{
    id: string;
    type: "success" | "error" | "warning" | "info";
    message: string;
  }>;
}

const loadThemeFromStorage = (): Theme => {
  try {
    const theme = localStorage.getItem("nexara_theme");
    return (theme as Theme) || "dark";
  } catch {
    return "dark";
  }
};

const initialState: UiState = {
  theme: loadThemeFromStorage(),
  sidebarOpen: false,
  notifications: [],
  unreadNotificationsCount: 0,
  searchOpen: false,
  profileDropdownOpen: false,
  notificationPanelOpen: false,
  activeModal: null,
  toasts: [],
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      localStorage.setItem("nexara_theme", action.payload);
      document.documentElement.classList.toggle(
        "light",
        action.payload === "light",
      );
    },
    toggleTheme: (state) => {
      const newTheme = state.theme === "dark" ? "light" : "dark";
      state.theme = newTheme;
      localStorage.setItem("nexara_theme", newTheme);
      document.documentElement.classList.toggle("light", newTheme === "light");
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, "id" | "createdAt">>,
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
      };
      state.notifications.unshift(notification);
      if (!notification.read) {
        state.unreadNotificationsCount += 1;
      }
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload,
      );
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadNotificationsCount = Math.max(
          0,
          state.unreadNotificationsCount - 1,
        );
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach((n) => (n.read = true));
      state.unreadNotificationsCount = 0;
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex(
        (n) => n.id === action.payload,
      );
      if (index !== -1) {
        if (!state.notifications[index].read) {
          state.unreadNotificationsCount = Math.max(
            0,
            state.unreadNotificationsCount - 1,
          );
        }
        state.notifications.splice(index, 1);
      }
    },
    setSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.searchOpen = action.payload;
    },
    setProfileDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.profileDropdownOpen = action.payload;
    },
    setNotificationPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.notificationPanelOpen = action.payload;
    },
    setActiveModal: (state, action: PayloadAction<string | null>) => {
      state.activeModal = action.payload;
    },
    addToast: (
      state,
      action: PayloadAction<{
        type: "success" | "error" | "warning" | "info";
        message: string;
      }>,
    ) => {
      state.toasts.push({
        id: Date.now().toString(),
        ...action.payload,
      });
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
  },
});

export const {
  setTheme,
  toggleTheme,
  toggleSidebar,
  setSidebarOpen,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  setSearchOpen,
  setProfileDropdownOpen,
  setNotificationPanelOpen,
  setActiveModal,
  addToast,
  removeToast,
} = uiSlice.actions;
export default uiSlice.reducer;
